/** Client API du module « Devis et fidélisation » (portail). Types issus du fragment docs/contrats/fragments/devis.yaml. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, nouvelleCleIdempotence, patch, post } from '../../../shared/api/client';

export type TypeCond = 'PALETTE' | 'CARTON' | 'PACK';
export type StatutDevisClient = 'DEMANDE' | 'EN_ETUDE' | 'PROPOSE' | 'ACCEPTE' | 'REFUSE' | 'EXPIRE' | 'ANNULE';
export type Frequence = 'AUCUNE' | 'HEBDOMADAIRE' | 'BIMENSUELLE' | 'MENSUELLE';

export interface DevisClient {
  id: string; reference: string; statut: StatutDevisClient; message: string | null;
  lignes: { id: string; produitId: string; libelle: string; sku: string; conditionnementType: TypeCond; quantite: number; prixProposeHtCentimes: number | null; totalHtCentimes: number | null }[];
  totalHtCentimes: number | null; valideJusqua: string | null; motifRefus: string | null; accordId: string | null;
  peutAccepter: boolean; peutAnnuler: boolean;
  historique: { date: string; action: string; statut: string; auteur: 'CLIENT' | 'MYRON' | 'SYSTEME' }[];
  creeLe: string; version: number;
}
export interface ProduitDevis { id: string; libelle: string; sku: string; ean: string | null; conditionnements: { type: TypeCond; unitesBase: number; minimum: number; multiple: number }[] }
export interface Favori { id: string; produitId: string; conditionnementType: TypeCond; libelle: string; sku: string; image: string | null; unitesBase: number | null; minimum: number; multiple: number; disponible: boolean; creeLe: string }
export interface ModeleCommande {
  id: string; nom: string; frequence: Frequence; prochaineEcheance: string | null; actif: boolean; aCommander: boolean; notifieLe: string | null;
  lignes: { produitId: string; libelle: string; sku: string; conditionnementType: TypeCond; quantite: number; disponible: boolean }[];
  creeLe: string; version: number;
}
export interface Page<T> { donnees: T[]; page: number; taille: number; total: number }

export const LIBELLE_FREQUENCE: Record<Frequence, string> = { AUCUNE: 'Ponctuelle (sans rappel)', HEBDOMADAIRE: 'Chaque semaine', BIMENSUELLE: 'Toutes les deux semaines', MENSUELLE: 'Chaque mois' };
export const LIBELLE_STATUT_DEVIS: Record<string, string> = {
  DEMANDE: 'Demande envoyée', EN_ETUDE: 'En étude', PROPOSE: 'Proposition reçue', EN_VALIDATION: 'En validation', ACCEPTE: 'Accepté', REFUSE: 'Refusé', EXPIRE: 'Expiré', ANNULE: 'Annulé',
};
export const TON_STATUT_DEVIS: Record<string, 'neutre' | 'succes' | 'alerte' | 'erreur' | 'info' | 'marque' | 'accent' | 'sable'> = {
  DEMANDE: 'sable', EN_ETUDE: 'info', EN_VALIDATION: 'alerte', PROPOSE: 'marque', ACCEPTE: 'succes', REFUSE: 'erreur', EXPIRE: 'neutre', ANNULE: 'neutre',
};
export const LIBELLE_ACTION_DEVIS: Record<string, string> = {
  DEMANDE: 'Demande envoyée', PRISE_EN_CHARGE: 'Prise en charge par MYRON', PROPOSE: 'Proposition de prix', ACCEPTE: 'Devis accepté', REFUSE: 'Proposition refusée',
  REFUSE_PAR_MYRON: 'Demande déclinée par MYRON', EXPIRE: 'Proposition expirée', ANNULE: 'Demande annulée',
  EXCEPTION_MARGE_DEMANDEE: 'Exception de marge demandée ◆', EXCEPTION_MARGE_APPROUVEE: 'Exception de marge approuvée', EXCEPTION_MARGE_REJETEE: 'Exception de marge rejetée', EXCEPTION_MARGE_EXPIREE: 'Demande d’exception expirée',
};

export const clesFidelite = {
  devis: ['pro', 'devis'] as const,
  unDevis: (id: string) => ['pro', 'devis', id] as const,
  favoris: ['pro', 'favoris'] as const,
  modeles: ['pro', 'modeles'] as const,
  panier: ['pro', 'panier'] as const,
};

export const useMesDevis = (page: number) => useQuery({ queryKey: [...clesFidelite.devis, page], queryFn: () => get<Page<DevisClient>>('/pro/devis', { page, taille: 20 }) });
export const useDevis = (id: string) => useQuery({ queryKey: clesFidelite.unDevis(id), queryFn: () => get<DevisClient>(`/pro/devis/${id}`) });
export const useFavoris = (actif = true) => useQuery({ queryKey: clesFidelite.favoris, queryFn: () => get<Favori[]>('/pro/favoris'), enabled: actif, staleTime: 30_000 });
export const useModeles = () => useQuery({ queryKey: clesFidelite.modeles, queryFn: () => get<ModeleCommande[]>('/pro/modeles') });

/** Ajout au panier via l'API du panier (prix recalculés côté serveur). */
export function useAjoutPanier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (l: { produitId: string; conditionnementType: TypeCond; quantite: number }) => post('/pro/panier/lignes', l),
    onSuccess: () => qc.invalidateQueries({ queryKey: clesFidelite.panier }),
  });
}

export function useMutationsDevis(id: string) {
  const qc = useQueryClient();
  const maj = (d: DevisClient) => { qc.setQueryData(clesFidelite.unDevis(id), d); void qc.invalidateQueries({ queryKey: clesFidelite.devis, exact: false }); };
  return {
    accepter: useMutation({
      mutationFn: (cle: string) => post<{ devis: DevisClient; nbLignesPanier: number }>(`/pro/devis/${id}/accepter`, undefined, { idempotence: cle }),
      onSuccess: (r) => { maj(r.devis); void qc.invalidateQueries({ queryKey: clesFidelite.panier }); },
    }),
    refuser: useMutation({ mutationFn: (motif: string) => post<DevisClient>(`/pro/devis/${id}/refuser`, motif ? { motif } : {}), onSuccess: maj }),
    annuler: useMutation({ mutationFn: () => post<DevisClient>(`/pro/devis/${id}/annuler`), onSuccess: maj }),
  };
}

export function useMutationsModeles() {
  const qc = useQueryClient();
  const rafraichir = () => qc.invalidateQueries({ queryKey: clesFidelite.modeles });
  return {
    creer: useMutation({ mutationFn: (c: { nom: string; frequence: Frequence; prochaineEcheance?: string; depuisPanier: true }) => post<ModeleCommande>('/pro/modeles', c), onSuccess: rafraichir }),
    modifier: useMutation({ mutationFn: ({ m, champs }: { m: ModeleCommande; champs: Partial<Pick<ModeleCommande, 'nom' | 'frequence' | 'actif'>> & { prochaineEcheance?: string } }) => patch<ModeleCommande>(`/pro/modeles/${m.id}`, champs, { version: m.version }), onSuccess: rafraichir }),
    supprimer: useMutation({ mutationFn: (id: string) => del(`/pro/modeles/${id}`), onSuccess: rafraichir }),
    charger: useMutation({
      mutationFn: ({ id, mode }: { id: string; mode: 'AJOUTER' | 'REMPLACER' }) => post<{ modele: ModeleCommande; nbLignesPanier: number; ignorees: { libelle: string }[] }>(`/pro/modeles/${id}/charger`, { mode }),
      onSuccess: () => { void rafraichir(); void qc.invalidateQueries({ queryKey: clesFidelite.panier }); },
    }),
  };
}

export { nouvelleCleIdempotence };
